import {
	addProductToCompany,
	createCompany,
	editCompany,
	exportEmployees,
	getCompanies,
	getCompaniesAccountManagers,
	getCompanyById,
	getCompanyInvoices,
	getCompanyList,
	getCompanyProducts,
	getCountries,
	getEmployeesCompany,
	getOpenDhcCompanies,
	updateCompanyStatus,
	updateLinkStatus,
} from "@/api/companies";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AddProductCompanyPayload, CreateEditCompanyPayload } from "../types";
import { useRef } from "react";
import { downloadBlob } from "@/shared/utils/csv";
import { showNotifications } from "@/shared/utils/notification";

export const useGetCompanies = (filter: Record<string, string | boolean | number>) => {
	return useQuery({
		queryKey: ["companies", filter],
		queryFn: () => getCompanies(filter),
	});
};

export const useGetCompanyById = (id?: string, enabled: boolean = true) => {
	const enableId = id && id !== "0" ? true : false;

	return useQuery({
		queryKey: ["company", id],
		queryFn: () => getCompanyById(id as string),
		enabled: enableId && enabled,
	});
};

export const useGetCompanyInvoices = (companyId: string, filter: Record<string, string | boolean | number>) => {
	return useQuery({
		queryKey: ["company-invoices", companyId, filter],
		queryFn: () => getCompanyInvoices(companyId, filter),
	});
};

export const useGetCompanyProducts = (
	companyId: string,
	filter: Record<string, string | boolean | number>,
	locale: string
) => {
	return useQuery({
		queryKey: ["company-products", companyId, filter, locale],
		queryFn: () => getCompanyProducts(companyId, filter, locale),
	});
};

export const useGetCompanyList = (type?: string) => {
	return useQuery({
		queryKey: ["company-list", type],
		queryFn: () => getCompanyList(type),
		staleTime: 0,
		gcTime: 0,
	});
};

export const useGetCompanyAccountManagers = () => {
	return useQuery({
		queryKey: ["company-account-managers"],
		queryFn: () => getCompaniesAccountManagers(),
	});
};

export const useCreateCompany = () => {
	return useMutation({
		mutationFn: (payload: CreateEditCompanyPayload) => createCompany(payload),
	});
};

export const useEditCompany = () => {
	return useMutation({
		mutationFn: (payload: CreateEditCompanyPayload) => editCompany(payload),
	});
};

export const useAddProductToCompany = () => {
	return useMutation({
		mutationFn: (payload: AddProductCompanyPayload) => addProductToCompany(payload),
	});
};

export const useUpdateCompanyStatus = () => {
	return useMutation({
		mutationFn: ({ companyId, status }: { companyId: number; status: string }) =>
			updateCompanyStatus(companyId, status),
	});
};

export const useEmployeesCompany = (companyId: string, filter: Record<string, string | boolean | number>) => {
	return useQuery({
		queryKey: ["employees-company", companyId, filter],
		queryFn: () => getEmployeesCompany(companyId, filter),
		enabled: !!companyId,
	});
};

export const useOpenDhcCompany = (companyId: string, filter: Record<string, string | boolean | number>) => {
	return useQuery({
		queryKey: ["open-dhc-company", companyId],
		queryFn: () => getOpenDhcCompanies(companyId, filter),
		enabled: !!companyId,
	});
};

export const useCountries = () => {
	return useQuery({
		queryKey: ["countries"],
		queryFn: () => getCountries(),
	});
};

export const useUpdateLinkStatus = () => {
	return useMutation({
		mutationFn: ({ companyId, isEnabled }: { companyId: number; isEnabled: boolean }) =>
			updateLinkStatus(companyId, isEnabled),
	});
};

export const useExportEmployees = () => {
	const abortControllerRef = useRef<AbortController | null>(null);
	const formatRef = useRef<"csv" | "xlsx">("csv");

	const { isPending, mutate } = useMutation({
		mutationFn: ({
			companyId,
			locale,
			format,
			signal,
		}: {
			companyId: string;
			locale: string;
			format: "csv" | "xlsx";
			signal: AbortSignal;
		}) => exportEmployees(companyId, locale, format, signal),
		onSuccess: (blob) => {
			downloadBlob(blob, `employees-export.${formatRef.current}`);
		},
		onError: (error: unknown) => {
			if (error instanceof Error && error.name === "CanceledError") {
				return;
			}
			showNotifications({ title: "Export Failed", message: "Failed to export DHC data.", successNotification: false });
		},
	});

	const handleExport = (companyId: string, locale: string, format: "csv" | "xlsx") => {
		formatRef.current = format;
		const controller = new AbortController();
		abortControllerRef.current = controller;
		mutate({ companyId, locale, format, signal: controller.signal });
	};

	const handleCancelExport = () => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
	};

	return {
		exportFormat: (isPending ? formatRef.current : undefined) as "csv" | "xlsx" | undefined,
		handleExport,
		handleCancelExport,
	};
};
